import { getLocaleNum } from '~~/common/utils';
import { formatPriceToStr } from '~~/common/utils/price';
import type { PostTranslationPluginProvides } from '~~/common/plugins/post-translation';
import {
  PACKAGE_UNIT_PIECE,
  PACKAGE_UNIT_WEIGHT,
} from '~~/common/config/packageUnit';

export const unitNames = {
  szt: PACKAGE_UNIT_PIECE,
  db: PACKAGE_UNIT_PIECE,
  piece: PACKAGE_UNIT_PIECE,
  kg: PACKAGE_UNIT_WEIGHT,
  weight: PACKAGE_UNIT_WEIGHT,
};

export function getAnaliticsUnit (
  {
    quantityType,
  }: {
    quantityType?: keyof typeof unitNames,
  },
) {
  if (quantityType !== undefined && unitNames[quantityType] === PACKAGE_UNIT_PIECE) {
    return 'db';
  } else if (quantityType !== undefined && unitNames[quantityType] === PACKAGE_UNIT_WEIGHT) {
    return 'kg';
  } else {
    throw new ErrorUnitNameConversion(quantityType);
  }
}

export function unitConverter<
  T extends string = keyof typeof unitNames,
> (
  arg: {
    from: {
      value: number,
      unit: NoInfer<T>,
    },
    to: keyof typeof unitNames,
    weightPerPiece: number,
  },
) {
  const {
    from: {
      value,
      unit,
    },
    to: targetUnit,
    weightPerPiece,
  } = arg;
  const o = { unit: targetUnit, value: 0 };
  if (unitNames[unit as keyof typeof unitNames] === undefined) {
    throw new ErrorUnitConversion(unit, value, targetUnit);
  }
  const validUnit = unit as keyof typeof unitNames;
  if (unitNames[targetUnit] === unitNames[validUnit]) {
    o.value = value;
  } else if (unitNames[targetUnit] === PACKAGE_UNIT_PIECE && unitNames[validUnit] === PACKAGE_UNIT_WEIGHT) { // kg -> db
    o.value = value / weightPerPiece;
  } else if (unitNames[targetUnit] === PACKAGE_UNIT_WEIGHT && unitNames[validUnit] === PACKAGE_UNIT_PIECE) { // db -> kg
    o.value = value * weightPerPiece;
  } else {
    throw new ErrorUnitConversion(validUnit, value, targetUnit);
  }
  return o;
}

export function itemToQuantity<
  T extends string = keyof typeof unitNames,
> (i?: {
  quantity?: {
    quantity?: number,
    type?: NoInfer<T>,
  },
  variant?: {
    loose?: {
      weightPerPiece?: number,
    } | null,
  },
}) {
  const unit = i?.quantity?.type;
  const weightPerPiece = i?.variant?.loose?.weightPerPiece;
  if (!i?.quantity?.quantity || !i?.variant?.loose || unit === undefined || weightPerPiece === undefined) {
    return 0;
  }
  return (
    unitConverter<T>({
      from: {
        value: i.quantity.quantity,
        unit,
      },
      to: 'piece',
      weightPerPiece,
    })?.value
  );
}

export function getQuantityStepSizeForUnit (
  variant: {
    loose: {
      loose: boolean,
    },
    cartInfo: {
      quantityStepSize: number,
    },
  },
  unit: keyof typeof unitNames,
) {
  return ((unitNames[unit] === PACKAGE_UNIT_WEIGHT || (unitNames[unit] === PACKAGE_UNIT_PIECE && variant.loose.loose === false))
    ? variant.cartInfo.quantityStepSize
    : 1
  );
}

export function productPackageUnitSizeStr2 (arg: {
  $awt: PostTranslationPluginProvides['awt'],
  locale: string,
  variant: {
    loose?: {
      loose: boolean,
    },
    packageInfo?: {
      packageSize?: number,
      packageUnit?: string,
      unitPrice?: {
        currency?: string,
        grossDiscounted?: number,
      },
    },
    cartInfo?: {
      quantityStepSize?: number,
    },
    roll?: {
      price?: {
        grossDiscounted?: number
        currency?: string,
      },
    },
  },
}) {
  const { variant, locale, $awt } = arg || {};
  let str = '';
  if (!locale || !variant || typeof variant !== 'object' || typeof $awt !== 'function') {
    return str;
  }
  const packageSize = ((typeof variant.packageInfo?.packageSize !== 'number')
    ? NaN
    : variant.packageInfo.packageSize
  );
  const packageUnit = variant.packageInfo?.packageUnit?.toLowerCase?.() || '';
  const qss = (typeof variant.cartInfo?.quantityStepSize === 'number'
    ? variant.cartInfo?.quantityStepSize
    : NaN
  );
  const isLoose = variant.loose?.loose || false;
  const qssIsNotOne = (qss || qss === 0) && qss !== 1;
  if (isLoose) {
    str += `1\xA0db kb. ${getLocaleNum(locale, qss * packageSize)}\xA0${packageUnit}`;
  } else {
    if (__AW_IS_SHOP__ || packageSize !== 1 || packageUnit !== 'db' || qssIsNotOne) {
      str += `${getLocaleNum(locale, packageSize)}\xA0${packageUnit}`;
    }
    if (qssIsNotOne) {
      str += ` | ${$awt(
        'aw.global.prod_order_unit',
        {
          quantity_step_size: `${getLocaleNum(locale, qss)}\xA0db`,
        },
      )}`;
    }
  }
  if (qssIsNotOne || isLoose) {
    if (variant.packageInfo?.unitPrice?.currency && variant.packageInfo?.unitPrice?.grossDiscounted) {
      const priceValue = ((variant.packageInfo.unitPrice.grossDiscounted * packageSize) + (variant.roll?.price?.grossDiscounted ?? 0)) * qss;
      str += ` (${formatPriceToStr({
        locale,
        currency: variant.packageInfo.unitPrice.currency,
        priceValue,
      })})`;
    }
  }
  return str.trim();
}

export function productPackageUnitSizeStr (arg: {
  locale: string,
  variant: {
    packageInfo?: {
      packageSize?: number,
      packageUnit?: string,
    },
    cartInfo?: {
      quantityStepSize?: number,
    },
  },
}) {
  const { variant, locale } = arg || {};
  let str = '';
  if (!locale || !variant || typeof variant !== 'object') {
    return str;
  }
  const packageSize = ((typeof variant.packageInfo?.packageSize !== 'number')
    ? NaN
    : variant.packageInfo.packageSize
  );
  const packageUnit = variant.packageInfo?.packageUnit?.toLowerCase?.() || '';
  const qss = (typeof variant.cartInfo?.quantityStepSize === 'number'
    ? variant.cartInfo?.quantityStepSize
    : NaN
  );
  const qssIsNotOne = (qss || qss === 0) && qss !== 1;
  if (qssIsNotOne) {
    str += `${getLocaleNum(locale, qss)}\xA0\xD7\xA0`;
  }
  if (__AW_IS_SHOP__ || packageSize !== 1 || packageUnit !== 'db' || qssIsNotOne) {
    str += `${getLocaleNum(locale, packageSize)}\xA0${packageUnit}`;
  }
  return str.trim();
}

class ErrorUnitConversion extends Error {
  override readonly name = 'ErrorUnitConversion';

  constructor (unit: unknown, value: unknown, targetUnit: unknown) {
    const msg = `value: ${value}, unit: ${unit} cannot be converted into ${targetUnit}`;
    super(msg);
  }
}

class ErrorUnitNameConversion extends Error {
  override readonly name = 'ErrorUnitNameConversion';

  constructor (unit: unknown) {
    const msg = `unit name: ${unit} cannot be converted.`;
    super(msg);
  }
}
